import React, { useState, useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, btnP, sp, rad, type, icBox } from "../../constants/styles";
import Ic from "../common/Ic";

const escCsv = (v) => { const s = String(v ?? ""); return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s; };

const downloadCsv = (rows, headers, filename) => {
  if (!rows.length) return false;
  const csv = [headers.join(","), ...rows.map(r => headers.map(h => escCsv(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  return true;
};

export default function ReportsPage() {
  const { t, h, go, props, tens, pays, mts, leases, ccs, flash } = useContext(Ctx);
  const [report, setReport] = useState(null);

  const now = new Date();
  const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const curMo = `${mNames[now.getMonth()]} ${now.getFullYear().toString().slice(-2)}`;

  // --- Stats ---
  const totalProps = props.length;
  const occupied = props.filter(p => p.occ).length;
  const vacant = totalProps - occupied;
  const totalRent = props.reduce((s, p) => s + (p.rent || 0), 0);
  const collected = pays.filter(p => p.st === "paid").reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
  const pending = pays.filter(p => p.st === "due").reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
  const openMaint = mts.filter(m => m.st === "open" || m.st === "assigned").length;
  const activeTenants = tens.filter(te => te.pid && !te.vac).length;

  const reports = [
    { id: "payments", icon: "credit-card", color: h.ac, title: t.paymentReport || "Payment Report", desc: "All rent payments with status, method, tenant" },
    { id: "properties", icon: "home", color: h.gn, title: t.propertyReport || "Property Report", desc: "Properties with occupancy, rent, type, tenant" },
    { id: "tenants", icon: "users", color: h.pp || h.ac, title: t.tenantReport || "Tenant Report", desc: "All tenants with rent, property, join date, status" },
    { id: "maintenance", icon: "wrench", color: h.wn, title: t.maintReport || "Maintenance Report", desc: "Requests with urgency, status, cost, assignee" },
    { id: "leases", icon: "file-text", color: h.gn, title: t.leaseReport || "Lease Report", desc: "Lease agreements with status, dates, tenant" },
    { id: "income", icon: "dollar", color: h.ac, title: t.incomeReport || "Income Summary", desc: "Monthly income breakdown by property" },
  ];

  const generateReport = (type) => {
    let success = false;
    const date = now.toISOString().slice(0, 10);

    switch (type) {
      case "payments": {
        const rows = pays.map(p => {
          const te = tens.find(x => x.id === p.tid);
          const pr = props.find(x => x.tid === p.tid);
          return { ID: p.id, Tenant: te?.name || "", Phone: te?.ph || "", Property: pr?.addr || "", Month: p.mo, Rent: p.amt || 0, CommonCharges: p.com || 0, Total: (p.amt || 0) + (p.com || 0), Status: p.st, Method: p.mth || "", Date: p.dt || "", PendingApproval: p.pending ? "Yes" : "No" };
        });
        success = downloadCsv(rows, ["ID", "Tenant", "Phone", "Property", "Month", "Rent", "CommonCharges", "Total", "Status", "Method", "Date", "PendingApproval"], `payments_${date}.csv`);
        break;
      }
      case "properties": {
        const rows = props.map(p => {
          const te = tens.find(x => x.id === p.tid);
          return { ID: p.id, Address: p.addr, Type: p.property_type || "apartment", Rooms: p.rooms, Rent: p.rent, Deposit: p.dep, Occupied: p.occ ? "Yes" : "No", Tenant: te?.name || "", TenantPhone: te?.ph || "", Amenities: (p.amen || []).join("; "), Latitude: p.latitude || "", Longitude: p.longitude || "", Photos: (p.photos || []).length };
        });
        success = downloadCsv(rows, ["ID", "Address", "Type", "Rooms", "Rent", "Deposit", "Occupied", "Tenant", "TenantPhone", "Amenities", "Latitude", "Longitude", "Photos"], `properties_${date}.csv`);
        break;
      }
      case "tenants": {
        const rows = tens.map(te => {
          const pr = props.find(x => x.id === te.pid);
          const tPays = pays.filter(x => x.tid === te.id);
          const due = tPays.filter(x => x.st === "due").reduce((s, x) => s + (x.amt || 0) + (x.com || 0), 0);
          const paid = tPays.filter(x => x.st === "paid").reduce((s, x) => s + (x.amt || 0) + (x.com || 0), 0);
          return { ID: te.id, Name: te.name, Phone: te.ph || "", Email: te.email || "", Property: pr?.addr || "Unassigned", Rent: pr?.rent || 0, JoinDate: te.join || "", VacateRequested: te.vac ? "Yes" : "No", TotalPaid: paid, TotalDue: due, PaymentCount: tPays.length };
        });
        success = downloadCsv(rows, ["ID", "Name", "Phone", "Email", "Property", "Rent", "JoinDate", "VacateRequested", "TotalPaid", "TotalDue", "PaymentCount"], `tenants_${date}.csv`);
        break;
      }
      case "maintenance": {
        const rows = mts.map(m => {
          const te = tens.find(x => x.id === m.tid);
          return { ID: m.id, Description: m.desc, Tenant: te?.name || "", Urgency: m.urg, Status: m.st, Date: m.dt || "", Assignee: m.asn_n || "", AssigneePhone: m.asn_p || "", Expense: m.exp_amt || 0, ChargedTo: m.exp_target || "", ExpenseApplied: m.exp_applied ? "Yes" : "No" };
        });
        success = downloadCsv(rows, ["ID", "Description", "Tenant", "Urgency", "Status", "Date", "Assignee", "AssigneePhone", "Expense", "ChargedTo", "ExpenseApplied"], `maintenance_${date}.csv`);
        break;
      }
      case "leases": {
        const rows = (leases || []).map(l => {
          const te = tens.find(x => x.id === l.tenant_id);
          const pr = props.find(x => x.id === l.property_id);
          return { ID: l.id, Tenant: te?.name || "", Property: pr?.addr || "", Status: l.status, StartDate: l.start_date || "", EndDate: l.end_date || "", Rent: l.rent || pr?.rent || "", Deposit: l.deposit || pr?.dep || "" };
        });
        success = downloadCsv(rows, ["ID", "Tenant", "Property", "Status", "StartDate", "EndDate", "Rent", "Deposit"], `leases_${date}.csv`);
        break;
      }
      case "income": {
        // Group payments by month
        const monthMap = {};
        pays.filter(p => p.st === "paid").forEach(p => {
          if (!monthMap[p.mo]) monthMap[p.mo] = { Month: p.mo, RentCollected: 0, CommonCharges: 0, Total: 0, Payments: 0 };
          monthMap[p.mo].RentCollected += (p.amt || 0);
          monthMap[p.mo].CommonCharges += (p.com || 0);
          monthMap[p.mo].Total += (p.amt || 0) + (p.com || 0);
          monthMap[p.mo].Payments += 1;
        });
        const rows = Object.values(monthMap).sort((a, b) => a.Month.localeCompare(b.Month));
        success = downloadCsv(rows, ["Month", "RentCollected", "CommonCharges", "Total", "Payments"], `income_summary_${date}.csv`);
        break;
      }
      default: break;
    }

    if (success) flash(t.csvExported || "Report downloaded!");
    else flash(t.noDataExport || "No data to export", "error");
  };

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> {t.back || "Back"}
    </button>

    <h2 style={{ ...type.h1, color: h.tx, margin: `0 0 ${sp.lg}px` }}>{t.reports || "Reports"}</h2>

    {/* Summary Stats */}
    <div data-coach="reports-period" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm, marginBottom: sp.xl }}>
      {[
        { label: t.totalCollected || "Collected", value: `\u20B9${collected.toLocaleString()}`, color: h.gn },
        { label: t.totalPending || "Pending", value: `\u20B9${pending.toLocaleString()}`, color: pending > 0 ? h.rd : h.gn },
        { label: t.occupancy || "Occupancy", value: `${occupied}/${totalProps}`, color: h.ac },
      ].map((s, i) => (
        <div key={i} style={{ ...crd(h), padding: sp.md, textAlign: "center" }}>
          <div style={{ ...type.h3, color: s.color }}>{s.value}</div>
          <div style={{ fontSize: 9, color: h.tt, marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>

    {/* Quick Stats Row */}
    <div style={{ ...crd(h), display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: sp.xs, padding: sp.md, marginBottom: sp.xl }}>
      {[
        { label: "Properties", value: totalProps },
        { label: "Tenants", value: activeTenants },
        { label: "Open Maint", value: openMaint },
        { label: "Leases", value: (leases || []).length },
      ].map((s, i) => (
        <div key={i} style={{ textAlign: "center", padding: sp.xs }}>
          <div style={{ ...type.h3, color: h.tx }}>{s.value}</div>
          <div style={{ fontSize: 9, color: h.tt }}>{s.label}</div>
        </div>
      ))}
    </div>

    {/* Report Cards */}
    <div data-coach="reports-export" style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
      {reports.map((r, i) => (
        <div key={r.id} onClick={() => generateReport(r.id)}
          style={{
            display: "flex", alignItems: "center", gap: sp.md,
            padding: `${sp.md}px ${sp.lg}px`, cursor: "pointer",
            borderBottom: i < reports.length - 1 ? `1px solid ${h.bd}` : "none",
            transition: "background 0.15s",
          }}>
          <div style={icBox(r.color, 36)}>
            <Ic n={r.icon} s={16} c={r.color} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{r.title}</div>
            <div style={{ ...type.xs, color: h.tt }}>{r.desc}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: sp.xs }}>
            <span style={bdg(`${r.color}15`, r.color)}>CSV</span>
            <Ic n="chevron-right" s={14} c={h.tt} />
          </div>
        </div>
      ))}
    </div>

    {/* Current Month Snapshot */}
    <div style={{ ...crd(h), marginTop: sp.xl }}>
      <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>{curMo} Snapshot</div>
      {(() => {
        const moPays = pays.filter(p => p.mo === curMo);
        const moPaid = moPays.filter(p => p.st === "paid");
        const moDue = moPays.filter(p => p.st === "due");
        const moCollected = moPaid.reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
        const moPending = moDue.reduce((s, p) => s + (p.amt || 0) + (p.com || 0), 0);
        const moTotal = moCollected + moPending;
        const pct = moTotal > 0 ? Math.round((moCollected / moTotal) * 100) : 0;
        return <>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: sp.sm }}>
            <span style={{ ...type.sm, color: h.tx }}>{t.collectionRate || "Collection Rate"}</span>
            <span style={{ ...type.sm, color: pct >= 80 ? h.gn : pct >= 50 ? h.wn : h.rd, fontWeight: 700 }}>{pct}%</span>
          </div>
          <div style={{ background: h.ib, borderRadius: 4, height: 8, overflow: "hidden", marginBottom: sp.md }}>
            <div style={{ background: pct >= 80 ? h.gn : pct >= 50 ? h.wn : h.rd, height: "100%", width: `${pct}%`, borderRadius: 4, transition: "width 0.5s" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", ...type.xs, color: h.ts }}>
            <span>{"\u2713"} {moPaid.length} paid ({"\u20B9"}{moCollected.toLocaleString()})</span>
            <span>{moDue.length} due ({"\u20B9"}{moPending.toLocaleString()})</span>
          </div>
        </>;
      })()}
    </div>
  </div>;
}
